import { MapAction } from "./delta";

/**
 * An "N" dimensional Key/Value Map, where an array of keys are
 * "flattened" into a single string-key.
 * 
 * ??less?? efficient method than Map2D, but more flexible
 */
export class MapND<Keys extends (string | number)[], Value> {
  readonly inner = new Map<string | number, [Keys, Value]>();
  readonly key_separator: string;

  constructor(initial: Iterable<readonly [Keys, Value]> = [], key_separator = ":") {
    this.key_separator = key_separator;

    for (const [key, value] of initial)
      this.inner.set(key.join(this.key_separator), [key, value])
  }

  set(key: Keys, value: Value) {
    const flatKey = key.join(this.key_separator);
    this.inner.set(flatKey, [key, value]);

    return this;
  }

  get(key: Keys) {
    const flatKey = key.join(this.key_separator);

    return this.inner.get(flatKey)?.[1];
  }
  delete(key: Keys) {
    const flatKey = key.join(this.key_separator);
    return this.inner.delete(flatKey);
  }

  has(key: Keys) {
    return this.inner.has(key.join(this.key_separator))
  }

  update(key: Keys, updater: (value: Value) => Value) {
    const value = this.get(key);
    if (!value)
      throw new Error(`No such key exists: "${key}"`);
    const result = updater(value);
    this.set(key, result);
    return result;
  }

  entries() {
    return this.inner.values();
  }

  *map<O>(mapFunction: (key: Keys, value: Value) => O) {
    for (const [key, value] of this.inner.values())
      yield mapFunction(key, value);
  }
  *filter<O>(filterFunction: (key: Keys, value: Value) => O) {
    for (const [key, value] of this.inner.values())
      if (filterFunction(key, value))
        yield [key, value] as const;
  }

  get size() {
    return this.inner.size;
  }

  clear() {
    this.inner.clear();
  }
}

export class Map2D<KeyX, KeyY, Value> {
  #inner = new Map<KeyY, Map<KeyX, Value>>();

  constructor(initialValues: Iterable<readonly [x: KeyX, y: KeyY, value: Value]> = []) {
    for (const [x, y, value] of initialValues)
      this.set(x, y, value);
  }

  *entries() {
    for (const [y, xMap] of this.#inner) {
      for (const [x, value] of xMap) {
        yield [x, y, value] as const;
      }
    }
  }
  *values() {
    for (const [, xMap] of this.#inner) {
      for (const [, value] of xMap) {
        yield value;
      }
    }
  }
  *keys(): Iterable<[KeyX, KeyY]> {
    for (const [y, xMap] of this.#inner) {
      for (const [x] of xMap) {
        yield [x, y] as [KeyX, KeyY];
      }
    }
  }

  has(x: KeyX, y: KeyY): boolean {
    let xMap = this.#inner.get(y);
    if (!xMap)
      return false;
    return xMap.has(x);
  }

  get(x: KeyX, y: KeyY): Value {
    let xMap = this.#inner.get(y);
    if (!xMap)
      throw new Error(`No such KeyY exists`);
    if (!xMap.has(x))
      throw new Error(`No such KeyX exists`);

    return xMap.get(x) as Value;
  }

  getOrNull(x: KeyX, y: KeyY): Value | null {
    let xMap = this.#inner.get(y);
    if (!xMap)
      return null;

    return xMap.get(x) || null;
  }

  set(x: KeyX, y: KeyY, value: Value): this {
    let xMap = this.#inner.get(y);
    if (!xMap) {
      xMap = new Map<KeyX, Value>()
      this.#inner.set(y, xMap);
    }
    xMap.set(x, value);

    return this;
  }

  delete(x: KeyX, y: KeyY) {
    let xMap = this.#inner.get(y);
    if (!xMap)
      return false;

    if (!xMap.has(x))
      return false;

    if (xMap.size === 1)
      this.#inner.delete(y)

    xMap.delete(x);
    return true;
  }

  map<TKeyX, TKeyY, TValue>(mapFunction: (x: KeyX, y: KeyY, value: Value) => [x: TKeyX, y: TKeyY, Value: TValue]) {
    function *map(entries: Iterable<readonly [x: KeyX, y: KeyY, value: Value]>) {
      for (const [x, y, value] of entries)
        yield mapFunction(x, y, value);
    }
    return new Map2D(map(this.entries()))
  }

  clone() {
    return new Map2D(this.entries());
  }
  clear() {
    this.#inner.clear();
  }
}

export const applyMap2DUpdate = <KeyX, KeyY, Value, Update>(
  map: Map2D<KeyX, KeyY, Value>,
  action: MapAction<[KeyX, KeyY], Value, Update>,
  update: (prev: Value, update: Update) => Value,
) => {
  switch (action.type) {
    case 'add':
      map.set(action.key[0], action.key[1], action.add);
      return;
    case 'remove':
      map.delete(action.key[0], action.key[1]);
      return;
    case 'update':
      const prev = map.get(action.key[0], action.key[1]);
      if (prev)
        map.set(action.key[0], action.key[1], update(prev, action.update));
      return;
  }
}

export class Array2D<Value> {
  readonly #values: Value[];

  readonly width: number;
  readonly height: number;

  constructor(width: number, height: number, initialValues: Value[]) {
    this.width = width;
    this.height = height;
    this.#values = initialValues
  }

  static fromSize<Value>(width: number, height: number, initialValue: Value) {
    return new Array2D(
      width, height,
      Array.from({ length: width * height }).map(() => initialValue)
    )
  }

  get length() {
    return this.width * this.height;
  }

  index(x: number, y: number) {
    return (y * this.width) + (x);
  }

  get(x: number, y: number) {
    return this.#values[this.index(x, y)]
  }
  set(x: number, y: number, value: Value) {
    this.#values[this.index(x, y)] = value;
  }

  *keys() {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        yield [x, y] as [number, number];
      }
    }
  }
}